import { useState, type ReactNode } from 'react';

import { CopyIcon } from './icons';

export interface CopyFieldProps {
  value: string;
  fallbackValue?: string;
  label?: ReactNode;
  buttonLabel?: string;
  className?: string;
  copyOnValueClick?: boolean;
  hideButton?: boolean;
  variant?: 'default' | 'chip';
  copiedLabel?: string;
}

const CopyField = ({
  value,
  fallbackValue,
  label,
  buttonLabel = 'Copy',
  className,
  copyOnValueClick = false,
  hideButton = false,
  variant = 'default',
  copiedLabel = 'Copied'
}: CopyFieldProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = fallbackValue ?? value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy value', error);
      setCopied(false);
    }
  };

  if (variant === 'chip') {
    const copyActionLabel =
      typeof label === 'string' && label.trim().length > 0
        ? label.trim().toLowerCase().startsWith('copy ')
          ? label.trim()
          : `Copy ${label.trim()}`
        : 'Copy value';
    return (
      <button
        type="button"
        className={['copy-field-chip', copied ? 'copy-field-chip--copied' : '', className].filter(Boolean).join(' ')}
        onClick={handleCopy}
        title={copied ? copiedLabel : copyActionLabel}
        aria-label={copied ? `${copiedLabel} to clipboard` : `${copyActionLabel} to clipboard`}
      >
        <span className="copy-field-chip__label">{copied ? copiedLabel : label ?? buttonLabel}</span>
        <CopyIcon size={14} aria-hidden="true" />
      </button>
    );
  }

  return (
    <div className={['copy-field', hideButton ? 'copy-field--value-only' : '', className].filter(Boolean).join(' ')}>
      {copyOnValueClick ? (
        <button
          type="button"
          className="copy-field__value-button"
          onClick={handleCopy}
          title={copied ? 'Copied' : 'Click to copy'}
          aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
        >
          <span className="copy-field__value">{label ?? value}</span>
          {hideButton ? <span className="copy-field__status">{copied ? 'Copied' : 'Click to copy'}</span> : null}
        </button>
      ) : (
        <div className="copy-field__value">{label ?? value}</div>
      )}
      {!hideButton ? (
        <button type="button" className="copy-field__button" onClick={handleCopy}>
          {copied ? 'Copied' : buttonLabel}
        </button>
      ) : null}
    </div>
  );
};

export default CopyField;
